import React, { memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Row, Col } from "react-bootstrap";
import FieldDetails from "./FieldDetails";
import { getFieldEquipmentGroupList, getHarvestSummary } from "../../../actions/dashboard";
import { SubNav } from "../../layout/submenu";
import { Unit } from "../../../helpers/unit";
import { searchWord } from "../../../helpers/common";
import { PLANTING_PLAN_TYPE_ID } from "../../../constants";

const FieldBody = memo(({ searchText }) => {
    // const { searchText } = props;
    const dispatch = useDispatch();
    const farmId = useSelector((state) => state.dashboard.farmId);
    const refreshCount = useSelector((state) => state.dashboard.refreshCount);
    const isRequesting = useSelector((state) => state.dashboard.field.fieldListById.isRequesting);
    const egListCount = useSelector((state) => Object.keys(state.dashboard.field.egListById.payload).length);
    const fieldListById = useSelector((state) => state.dashboard.field.fieldListById.payload);
    let selectedFieldIds = useSelector((state) => state.dashboard.field.selectedFieldIds);
    const workedFields = useSelector((state) => state.dashboard.field.workedFields);
    const sortBy = useSelector((state) => state.dashboard.field.sortBy);
    const isRequestingWorkedAcres = useSelector((state) => state.dashboard.field.workedAcresById.isRequesting);
    const { rangeId, custom } = useSelector((state) => state.analyze.searchParams);
    // const timezone = useSelector(state => state.farm.currentFarm.timezone_identifier);
    const isEnableHarvestProjection = useSelector((state) => state.farm.currentFarm.is_harvest_projection_enable);
    const harvestSummary = useSelector((state) => state.dashboard.harvestSummary);
    const isRequestingHarvestSummary = useSelector(state => state.dashboard.harvestSummary.isRequesting);
    const workedAcresById = useSelector((state) => state.dashboard.field.workedAcresById.payload);
    const tareLoadHistory = useSelector((state) => state.dashboard.tareLoadHistory);
    const selectedPlanTypeId = useSelector((state) => state.plan.selectedPlanType);
    const [harvestSummaryList, setHarvestSummaryList] = useState(null);

    const unit = useMemo(() => Unit.getInstance(), []);
    let workedIds = [];
    const isToday = moment().format("DD MMM YYYY") === moment(custom.from).format("DD MMM YYYY");

    if (searchText) {
        let filteredSelectedFields = [];

        if (selectedFieldIds.length > 0) {
            selectedFieldIds.forEach((e) => {
                filteredSelectedFields.push(fieldListById[e]);
            });
        }

        let fieldList = filteredSelectedFields && filteredSelectedFields.filter((item) => (fieldListById[item.geo_shape_uuid].geo_shape_name && searchWord(fieldListById[item.geo_shape_uuid].geo_shape_name, searchText)) || (fieldListById[item.geo_shape_uuid].regionDetails.crop_name && searchWord(fieldListById[item.geo_shape_uuid].regionDetails.crop_name, searchText)));
        selectedFieldIds = fieldList.map((e) => {
            return e.geo_shape_uuid;
        });
    }

    useEffect(() => {
        if (!isRequestingHarvestSummary) {
            const payload = harvestSummary.payload;
            setHarvestSummaryList({ ...payload });
        }
    }, [harvestSummary]);

    useEffect(() => {
        if (farmId && egListCount === 0) dispatch(getFieldEquipmentGroupList({ farmId }));
    }, [farmId]);

    const isRequestingFields = isRequesting || (selectedFieldIds.length === 0 && isRequestingWorkedAcres);

    useEffect(() => {
        if ((selectedPlanTypeId && selectedPlanTypeId !== PLANTING_PLAN_TYPE_ID) &&
            isEnableHarvestProjection && fieldListById && Object.keys(fieldListById).length !== 0) {
            // const startUTC = dateToUTC(custom.from, timezone);
            // const endUTC = dateToUTC(custom.to, timezone);
            let geoRegIds = selectedFieldIds.map((id) => fieldListById[id].regionDetails.geo_region_uuid);
            let params = {
                geo_region_uuid: geoRegIds.toString(),
                // start_date: startUTC.format(),
                // end_date: endUTC.format()
            };

            if (geoRegIds.length !== 0) dispatch(getHarvestSummary(params));
        }
    }, [fieldListById, rangeId, refreshCount, selectedPlanTypeId, isEnableHarvestProjection]);

    const getSummary = (harvestList, type) => {
        if (harvestList && harvestList.hasOwnProperty("summary")) {
            if (type === "worked") {
                let worked = 0;
                if (harvestList.summary.hasOwnProperty("harvest_acreage")) worked = getHarvestValue(harvestList, "harvest_acreage");
                if (harvestList.summary.hasOwnProperty("harvested_acreage")) worked = getHarvestValue(harvestList, "harvested_acreage");
                return worked !== 0 ? unit.toArea(worked, 0) : 0;
            }
            if (type === "percentage") {
                let worked = 0;
                if (harvestList.summary.hasOwnProperty("harvest_acreage")) worked = unit.toArea(getHarvestValue(harvestList, "harvest_acreage"), 0);
                if (harvestList.summary.hasOwnProperty("harvested_acreage")) worked = unit.toArea(getHarvestValue(harvestList, "harvested_acreage"), 0);
                return worked !== 0 ? ((worked / unit.toArea(getHarvestValue(harvestList, "total_acreage"), 0)) * 100).toFixed(0) : 0;
            }
            if (type === "load") {
                let harvesteLoads = 0;
                if (harvestList.summary.hasOwnProperty("harvest_loads")) harvesteLoads = getHarvestValue(harvestList, "harvest_loads");
                if (harvestList.summary.hasOwnProperty("harvested_loads")) harvesteLoads = getHarvestValue(harvestList, "harvested_loads");
                return harvesteLoads;
            }
        } else {
            return 0;
        }
    };

    const getWorkedPercentage = (fieldId) => {
        const workTypeId = 2;
        const workDetails = workedAcresById[`${fieldId}_${workTypeId}`] ? workedAcresById[`${fieldId}_${workTypeId}`] : null;
        const percentage = workDetails ? workDetails.percentage : 0;
        return percentage > 100 ? 100 : unit.toFixedOf(percentage, 0);
    };

    const getHarvestValue = (harvestList, key) => {
        let harvestSummary = harvestList && harvestList.hasOwnProperty("summary") ? harvestList.summary : null;
        if (!harvestSummary) return 0;
        return harvestList.summary[key] ? harvestList.summary[key] : 0;
    };

    let newSelectedFieldIds = [];
    for (const selectedId of selectedFieldIds) {
        let fieldObj = {};
        const geoRegionId = fieldListById && fieldListById[selectedId] && fieldListById[selectedId].regionDetails && fieldListById[selectedId].regionDetails.geo_region_uuid ? fieldListById[selectedId].regionDetails.geo_region_uuid : null;
        if (geoRegionId && isEnableHarvestProjection) {
            const harvestSummaryList = harvestSummary.payload;
            let harvestList = harvestSummaryList && harvestSummaryList.hasOwnProperty(geoRegionId) ? harvestSummaryList[geoRegionId] : null;
            const percentage = getSummary(harvestList, "percentage");

            fieldObj = {
                selected_field_id: selectedId,
                work_analysis_start_time: harvestList && harvestList.work_analysis_start_time ? harvestList.work_analysis_start_time : null,
                end: harvestList && harvestList.end ? harvestList.end : null,
                percentage,
            };
        } else {
            fieldObj = {
                selected_field_id: selectedId,
                work_analysis_start_time: null,
                end: null,
                percentage: !isEnableHarvestProjection ? getWorkedPercentage(selectedId) : 0,
            };
        }

        newSelectedFieldIds.push(fieldObj);

        if (workedFields && workedFields.length > 0 && workedFields.indexOf(selectedId) > -1) {
            workedIds.push({ selected_field_id: fieldObj.selected_field_id, percentage: fieldObj.percentage });
        }
    }

    const filterFieldIds = (workStatus, fieldIds) => {
        const filteredFieldIds =
            Object.keys(fieldListById).length > 0 &&
            fieldIds &&
            fieldIds.filter((id) => {
                return fieldListById[id].lastWorkStatus.work_status === workStatus;
            });
        return filteredFieldIds;
    };

    const getFieldIdsByPercentage = (sortBy, fieldIds) => {
        if (fieldIds && fieldIds.length === 0) return [];
        const completedFields = fieldIds.filter((e) => Number(e.percentage) === 100).map((e) => e.selected_field_id);

        if (sortBy.name === "percentage") {
            if (sortBy.type === "desc") {
                const nonCompletedFieldIds = fieldIds
                    .filter((e) => Number(e.percentage) !== 100)
                    .sort((a, b) => b.percentage - a.percentage)
                    .map((e) => e.selected_field_id);
                return [...nonCompletedFieldIds, ...completedFields];
            } else {
                const nonCompletedFieldIds = fieldIds
                    .filter((e) => Number(e.percentage) !== 100 && Number(e.percentage) !== 0)
                    .sort((a, b) => a.percentage - b.percentage)
                    .map((e) => e.selected_field_id);
                const remainingFields = [...fieldIds.filter((e) => Number(e.percentage) === 0).map((e) => e.selected_field_id), ...completedFields];
                return [...nonCompletedFieldIds, ...remainingFields];
            }
        }

        if (sortBy.name === "date") {
            if (sortBy.type === "desc") {
                const nonCompletedFieldIds = fieldIds
                    .filter((e) => Number(e.percentage) !== 100)
                    .sort((a, b) => new Date(b.end).valueOf() - new Date(a.end).valueOf())
                    .map((e) => e.selected_field_id);
                return [...nonCompletedFieldIds, ...completedFields];
            } else {
                const nonCompletedFieldIds = fieldIds
                    .filter((e) => Number(e.percentage) !== 100)
                    .sort((a, b) => new Date(a.end).valueOf() - new Date(b.end).valueOf())
                    .map((e) => e.selected_field_id);
                return [...nonCompletedFieldIds, ...completedFields];
            }
        }

        if (sortBy.name === "today") {
            const nonCompletedFieldIds = fieldIds
                .filter((e) => Number(e.percentage) !== 100)
                .sort((a, b) => b.percentage - a.percentage)
                .map((e) => e.id);
            return [...nonCompletedFieldIds, ...completedFields];
        }

        if (sortBy.name === "name") {
            let nonCompletedFieldIds = fieldIds
                .filter((e) => Number(e.percentage) !== 100)
                .map((e) => ({
                    id: e.selected_field_id,
                    name: fieldListById[e.selected_field_id].geo_shape_name,
                }));

            if (sortBy.type === "asc") {
                nonCompletedFieldIds = nonCompletedFieldIds
                    .sort(function (a, b) {
                        if (a.name < b.name) {
                            return -1;
                        }
                        if (a.name > b.name) {
                            return 1;
                        }
                        return 0;
                    })
                    .map((e) => e.id);
            } else {
                nonCompletedFieldIds = nonCompletedFieldIds
                    .sort(function (a, b) {
                        if (a.name > b.name) {
                            return -1;
                        }
                        if (a.name < b.name) {
                            return 1;
                        }
                        return 0;
                    })
                    .map((e) => e.id);
            }

            return [...nonCompletedFieldIds, ...completedFields];
        }
    };

    // newSelectedFieldIds = isEnableHarvestProjection ? newSelectedFieldIds.sort((a, b) => new Date(b.end).valueOf() - new Date(a.end).valueOf()).map((e) => e.selected_field_id) : newSelectedFieldIds.map((e) => e.selected_field_id);
    newSelectedFieldIds = newSelectedFieldIds ? getFieldIdsByPercentage(sortBy, newSelectedFieldIds) : [];

    // workedIds = workedIds && workedIds.length > 0 ? workedIds.sort((a, b) => b.percentage - a.percentage).map(e => e.id) : []
    workedIds = workedIds && workedIds.length > 0 ? getFieldIdsByPercentage(sortBy, workedIds) : [];

    const activeSelectedFieldIds = useMemo(() => filterFieldIds(1, newSelectedFieldIds), [newSelectedFieldIds]);
    const completedSelectedFieldIds = useMemo(() => filterFieldIds(4, newSelectedFieldIds), [newSelectedFieldIds]);

    return (
        <>
            <Row className={`field-item-list ${(newSelectedFieldIds.length === 0 || isRequestingFields) && "align-items-center"}`}>
                {newSelectedFieldIds.length === 0 || isRequestingFields ? (
                    <Col className="no-item-found">{isRequestingFields ? "Loading..." : "No Records Found"}</Col>
                ) : (
                    (selectedPlanTypeId && selectedPlanTypeId !== PLANTING_PLAN_TYPE_ID) ? <Col className="p-0">
                        {isToday && workedIds && workedIds.length > 0 && <SubNav.Divider name={"Today"} className="completed-fields" />}
                        {isToday && workedIds && workedIds.length > 0 && (
                            <Row className="m-0">
                                {workedIds &&
                                    workedIds.map((id, index) => {
                                        return <FieldDetails key={index} shapeRefId={id} shapeDetails={fieldListById[id] ? fieldListById[id] : null} tareLoadHistory={tareLoadHistory} harvestSummaryList={harvestSummaryList} isCompleted={true} />;
                                    })}
                            </Row>
                        )}

                        {isToday && workedIds && workedIds.length > 0 && activeSelectedFieldIds && activeSelectedFieldIds.length > 0 && <SubNav.Divider name={"Historical"} className="completed-fields" />}
                        <Row className="m-0">
                            {activeSelectedFieldIds &&
                                activeSelectedFieldIds.map((id, index) => {
                                    return <FieldDetails key={index} shapeRefId={id} shapeDetails={fieldListById[id] ? fieldListById[id] : null} tareLoadHistory={tareLoadHistory} harvestSummaryList={harvestSummaryList} isCompleted={true} />;
                                })}
                        </Row>
                        {completedSelectedFieldIds.length > 0 && <SubNav.Divider name={"Completed"} className="completed-fields" />}
                        {completedSelectedFieldIds.length > 0 && (
                            <Row className="m-0">
                                {completedSelectedFieldIds &&
                                    completedSelectedFieldIds.map((id, index) => {
                                        return <FieldDetails key={index} shapeRefId={id} shapeDetails={fieldListById[id] ? fieldListById[id] : null} tareLoadHistory={tareLoadHistory} harvestSummaryList={harvestSummaryList} isCompleted={false} />;
                                    })}
                            </Row>
                        )}
                    </Col>
                        : <Col className="p-0">
                            <Row className="m-0">
                                {activeSelectedFieldIds && [...activeSelectedFieldIds, ...completedSelectedFieldIds].map((id, index) => {
                                    return <FieldDetails key={index} shapeRefId={id} shapeDetails={fieldListById[id] ? fieldListById[id] : null} tareLoadHistory={tareLoadHistory} harvestSummaryList={harvestSummaryList} isCompleted={true} />;
                                })}
                            </Row>
                        </Col>
                )}
            </Row>
        </>
    );
});

export default FieldBody;
